import React, { useState, useEffect, useRef } from 'react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { useAuthToken } from '../utils/authTokenManager';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User, 
  Trash2, 
  Lightbulb,
  Zap,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Wrench,
  DollarSign,
  Edit3,
  Check,
  RotateCcw
} from 'lucide-react';
import { PROCOGIA_COLORS } from '../utils/themeUtils';
import axios from 'axios';

const ChatPage = () => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0Context();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sampleQuestions, setSampleQuestions] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editedQuestionText, setEditedQuestionText] = useState('');
  const messagesEndRef = useRef(null);

  // Demo prompt suggestions based on the document
  const defaultQuestions = [
    "What equipment needs immediate attention?",
    "Show me today's critical alerts",
    "Which machines have the highest failure risk?",
    "Generate a maintenance schedule for this week",
    "What's the overall equipment efficiency?",
    "Identify cost-saving opportunities",
    "Show predictive maintenance insights",
    "Which equipment should I prioritize?"
  ];

  const actionTemplates = [
    { icon: <AlertTriangle className="h-4 w-4" />, text: "Check Critical Alerts", color: "text-red-600" },
    { icon: <Wrench className="h-4 w-4" />, text: "Equipment Status", color: "project-info" },
    { icon: <TrendingUp className="h-4 w-4" />, text: "Performance Analytics", color: "project-blue" },
    { icon: <Calendar className="h-4 w-4" />, text: "Schedule Maintenance", color: "project-green" },
    { icon: <DollarSign className="h-4 w-4" />, text: "Cost Analysis", color: "text-yellow-600" },
    { icon: <Zap className="h-4 w-4" />, text: "Efficiency Report", color: "text-purple-600" }
  ];

  const chatAuthToken = useAuthToken(isAuthenticated && user ? user : null, getAccessTokenSilently);

  useEffect(() => {
    setSampleQuestions(defaultQuestions);
    setQuickActions(actionTemplates);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;
    
    // Try to get authentication token
    let currentToken = chatAuthToken;
    
    if (!currentToken) {
      setIsLoading(true);
      try {
        console.log('🔐 Fetching auth token for chat...');
        currentToken = await getAccessTokenSilently();
        if (!currentToken) {
          throw new Error('No token received');
        }
        console.log('✅ Auth token received for chat');
      } catch (error) {
        console.error('❌ Auth token fetch failed:', error);
        setIsLoading(false);
        const errorMessage = {
          id: Date.now(),
          text: 'Unable to authenticate. Please try refreshing the page and signing in again.',
          sender: 'bot',
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }
    }

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/chat/message`, 
        { 
          message: message
        },
        {
          headers: { 
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const botMessage = {
        id: Date.now() + 1,
        text: response.data.response || response.data.message || 'I apologize, but I encountered an issue processing your request.',
        sender: 'bot',
        timestamp: new Date(),
        suggestions: response.data.suggestions || null
      };

      setMessages(prev => [...prev, botMessage]);
      
      if (response.data.suggestions) {
        setSuggestedQuestions(response.data.suggestions);
      }

    } catch (error) {
      console.error('Chat error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: `I'm having trouble connecting to the AI service right now. Please try again in a moment. Error: ${error.response?.data?.error || error.message}`,
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionClick = (question) => {
    setInputMessage(question);
    // Focus the input field after setting the message
    setTimeout(() => {
      const textArea = document.querySelector('textarea[placeholder*="Ask me about equipment"]');
      if (textArea) {
        textArea.focus();
        // Move cursor to end of text
        textArea.setSelectionRange(textArea.value.length, textArea.value.length);
      }
    }, 100);
  };

  const clearChat = () => {
    setMessages([]);
    setSuggestedQuestions(null);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setEditedQuestionText(question);
  };

  const saveEditedQuestion = () => {
    if (editedQuestionText.trim()) {
      setSampleQuestions(prev => 
        prev.map(q => q === editingQuestion ? editedQuestionText : q)
      );
    }
    setEditingQuestion(null);
    setEditedQuestionText('');
  };

  const cancelEditQuestion = () => {
    setEditingQuestion(null);
    setEditedQuestionText('');
  };

  const formatMessage = (text) => {
    return text.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0"
        style={{ backgroundColor: PROCOGIA_COLORS.background }}
      >
        <div className="flex items-center space-x-3">
          <div 
            className="p-3 rounded-full"
            style={{ backgroundColor: PROCOGIA_COLORS.green }}
          >
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: PROCOGIA_COLORS.darkGrey }}>
              AI Manufacturing Assistant
            </h1>
            <p className="text-sm" style={{ color: PROCOGIA_COLORS.lightGrey }}>
              Powered by Advanced ML - Ask me anything about your manufacturing operations
            </p>
          </div>
        </div>
        <button
          onClick={clearChat}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: PROCOGIA_COLORS.lightGrey,
            color: 'white'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = PROCOGIA_COLORS.darkGrey;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = PROCOGIA_COLORS.lightGrey;
          }}
        >
          <Trash2 className="h-4 w-4" />
          <span>Clear Chat</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Messages Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <Bot className="h-16 w-16 mx-auto mb-4" style={{ color: PROCOGIA_COLORS.green }} />
                <h2 className="text-xl font-semibold mb-2" style={{ color: PROCOGIA_COLORS.darkGrey }}>
                  Welcome to your AI Manufacturing Assistant
                </h2>
                <p className="text-gray-600 mb-6">
                  I can help you with equipment monitoring, predictive maintenance, alerts, and operational insights.
                </p>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuestionClick(action.text)}
                      className="flex items-center space-x-2 p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                      style={{
                        borderColor: PROCOGIA_COLORS.lightGrey,
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = PROCOGIA_COLORS.green;
                        e.target.style.backgroundColor = PROCOGIA_COLORS.background;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = PROCOGIA_COLORS.lightGrey;
                        e.target.style.backgroundColor = 'white';
                      }}
                    >
                      <span style={{ color: PROCOGIA_COLORS.green }}>
                        {action.icon}
                      </span>
                      <span className="text-sm font-medium" style={{ color: PROCOGIA_COLORS.darkGrey }}>
                        {action.text}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.sender === 'bot' && (
                    <div 
                      className="p-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PROCOGIA_COLORS.green }}
                    >
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                      message.sender === 'user'
                        ? 'text-white'
                        : message.isError
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-gray-50 text-gray-800'
                    }`}
                    style={message.sender === 'user' ? {
                      backgroundColor: PROCOGIA_COLORS.blue
                    } : {}}
                  >
                    <div className="text-sm">
                      {formatMessage(message.text)}
                    </div>
                    <div className="text-xs mt-2 opacity-70">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                  {message.sender === 'user' && (
                    <div 
                      className="p-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PROCOGIA_COLORS.darkGrey }}
                    >
                      <User className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="flex items-start space-x-3">
                <div 
                  className="p-2 rounded-full"
                  style={{ backgroundColor: PROCOGIA_COLORS.green }}
                >
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask me about equipment status, maintenance schedules, alerts, or any operational questions..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-offset-2 focus:outline-none"
                  style={{ 
                    focusRingColor: PROCOGIA_COLORS.green,
                    minHeight: '60px',
                    maxHeight: '120px'
                  }}
                  rows={2}
                />
              </div>
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                style={{
                  backgroundColor: PROCOGIA_COLORS.green,
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = PROCOGIA_COLORS.green;
                  }
                }}
              >
                <Send className="h-4 w-4" />
                <span>Send</span>
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar with Sample Questions */}
        <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 overflow-y-auto">
          <h3 className="font-semibold mb-4 flex items-center space-x-2" style={{ color: PROCOGIA_COLORS.darkGrey }}>
            <Lightbulb className="h-5 w-5" style={{ color: PROCOGIA_COLORS.green }} />
            <span>Quick Questions</span>
          </h3>
          
          <div className="space-y-3">
            {sampleQuestions.map((question, index) => (
              <div key={index} className="group">
                {editingQuestion === question ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedQuestionText}
                      onChange={(e) => setEditedQuestionText(e.target.value)}
                      className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-offset-2 focus:outline-none resize-none"
                      style={{ focusRingColor: PROCOGIA_COLORS.green }}
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={saveEditedQuestion}
                        className="p-1 text-green-600 hover:text-green-700"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEditQuestion}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => handleQuestionClick(question)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors bg-white hover:bg-gray-50 text-sm"
                    >
                      {question}
                    </button>
                    <button
                      onClick={() => handleEditQuestion(question)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity"
                    >
                      <Edit3 className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {suggestedQuestions && (
            <div className="mt-8">
              <h3 className="font-semibold mb-4 flex items-center space-x-2" style={{ color: PROCOGIA_COLORS.darkGrey }}>
                <TrendingUp className="h-5 w-5" style={{ color: PROCOGIA_COLORS.blue }} />
                <span>Suggested Follow-ups</span>
              </h3>
              <div className="space-y-2">
                {suggestedQuestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionClick(suggestion)}
                    className="w-full text-left p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors text-sm border border-blue-200"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage; 